import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import verifyOTP from "../assets/verify-OTP.png";
import Button from "../components/Button";
import "../styles/global.css";
import { useNotification } from "../context/NotificationContext";
import {
  handleApiResponse,
  handleApiError,
} from "../utils/notificationHelpers";
import authApi from "../apiServices/auth";

const VerifyOtp = () => {
  const length = 4;
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const [timeLeft, setTimeLeft] = useState(60); // 60 seconds countdown
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const inputRefs = useRef([]);
  const { notify } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    // Retrieve email from localStorage (saved during forgot password)
    const savedEmail = localStorage.getItem("resetEmail");
    if (!savedEmail) {
      notify.warning("Please start from the forgot password page");
      navigate("/forgot-password");
    } else {
      setEmail(savedEmail);
    }
  }, [navigate, notify]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      notify.info("OTP expired. Please request a new one.");
    }
  }, [timeLeft, notify]);

  const handleResendOTP = async () => {
    try {
      setLoading(true);
      if (!email) {
        notify.warning("Email not found");
        navigate("/forgot-password");
        return;
      }

      const result = await authApi.forgotPassword(email);

      if (handleApiResponse(result, notify, "OTP sent", "Failed to send OTP")) {
        setTimeLeft(60); // Reset timer
      }
    } catch (error) {
      handleApiError(error, notify, "otp");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpComplete = (otpValue) => {
    // We'll verify the OTP in handleVerifyOTP
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    const otpValue = otp.join("");
    if (otpValue.length !== length) {
      notify.warning("Enter all OTP digits");
      return;
    }

    try {
      setLoading(true);
      const result = await authApi.verifyOTP(email, otpValue);

      if (
        handleApiResponse(
          result,
          notify,
          "OTP verified",
          "OTP verification failed"
        )
      ) {
        navigate("/reset-password");
      }
    } catch (error) {
      handleApiError(error, notify, "otp");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    if (isNaN(value)) return;
    let newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }

    if (newOtp.every((digit) => digit !== "")) {
      handleOtpComplete(newOtp.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <div className="flex justify-center gap-14 h-screen bg-[#FEF3E7] padding-x">
      {/* Left Side - Image */}
      <div className="hidden md:flex items-center justify-center md:w-2/5 lg:w-1/2">
        <img
          src={verifyOTP}
          alt="Verify OTP"
          className="max-w-xs md:max-w-md object-contain"
        />
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full md:w-3/5 lg:w-3/5 xl:w-2/5 flex flex-col items-center justify-center xl:p-2 bg-white shadow-lg">
        <img
          src="/Logo.png"
          alt="Logo"
          className="w-16 md:w-20 lg:w-24 mx-auto mb-12 md:mb-16"
        />
        <h2 className="text-4xl md:text-3xl xl:text-4xl font-bold mb-10">
          Verify Your OTP
        </h2>
        <p className="mb-8 text-center md:w-[75%] lg:w-[70%] xl:w-[65%] text-lg">
          Please enter the code you received below
        </p>

        <form className="w-full px-6" onSubmit={handleVerifyOTP}>
          <div className="flex gap-4 justify-center items-center">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                value={digit}
                maxLength="1"
                className="w-10 h-10 text-center border-b-2 border-black text-2xl focus:outline-none focus:border-orange-500 transition"
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
            ))}
          </div>

          <div className="mt-6 mb-5">
            <input
              type="password"
              placeholder="Enter your new password"
              className="w-full px-3 py-2.5 border border-black rounded-md focus:ring-2 focus:ring-orange-500 placeholder:text-gray-500"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>

          <div className="mt-4 text-gray-500 mb-8 flex justify-center items-center gap-2">
            {timeLeft > 0 ? (
              <>
                Resend OTP In{" "}
                <span className="font-semibold text-lg text-gray-800">{`00:${
                  timeLeft < 10 ? "0" : ""
                }${timeLeft}`}</span>
              </>
            ) : (
              <button
                type="button"
                onClick={handleResendOTP}
                className="text-orange-500 hover:text-orange-700 underline font-medium"
                disabled={loading}
              >
                Resend OTP
              </button>
            )}
          </div>

          <Button
            text={loading ? "Processing..." : "Verify & Reset Password"}
            className="mt-2 w-full"
            type="submit"
            disabled={loading}
          />
        </form>
      </div>
    </div>
  );
};

export default VerifyOtp;
